import React, { useState } from 'react';
import PlusF  from '@decisiv/iconix/lib/components/PlusCircle';
import TrashF from '@decisiv/iconix/lib/components/Trash';
import { Button, Flex, Tabs, Tab } from '@decisiv/ui-components';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import AdminPage          from './components/AdminPage';
import AdminTable         from './components/AdminTable';
import AdminFilters       from './components/AdminFilters';
import AdminPanel         from './components/AdminPanel';
import AdminConfirmModal  from './components/AdminConfirmModal';
import AssocPanel         from './components/AssocPanel';
import SubscriptionPanel  from './components/SubscriptionPanel';
import ErrorBox           from './components/ErrorBox';
import FormFields         from './components/FormFields';
import useAdminResource  from './hooks/useAdminResource';
import useAdminMutation  from './hooks/useAdminMutation';
import { renderCell } from './utils/cells';

/**
 * Factory that turns a resource config into ready-to-mount Index, Show, New,
 * and Edit React components. Adding a new admin resource means writing a config
 * object (and optionally a controller), not new JSX.
 *
 * ─── Config shape ────────────────────────────────────────────────────────────
 * {
 *   name:        'Dealer',
 *   endpoint:    '/admin_kit/dealers',
 *   modelKey:    'dealer',           Rails param key (required for forms)
 *   titleField?: 'name',             Field used as record title (defaults to name/decisiv_id/id)
 *
 *   columns:  [{ name, title, type?, render? }],
 *   filters:  [{ name, label, type?, options? }],
 *   attributes?: [{ name, label, type?, render? }],
 *   panels?:     [AssocPanel config],
 *   tabs?:       [{ id, title, attributes, panels? }],
 *
 *   form?: {
 *     fields: [
 *       { name, label, type: 'text'|'textarea'|'checkbox'|'select'|'typeahead',
 *         defaultValue?, required?, helpMessage?, rows?, span?,
 *         options?,        // for select: array or (props) => array
 *         searchEndpoint?, // for typeahead: URL for ?q= search
 *         displayField?,   // for typeahead: record field holding display value on edit
 *       }
 *     ]
 *   },
 * }
 */
const createAdminResource = (config) => {
  const {
    name,
    endpoint,
    modelKey,
    titleField,
    columns     = [],
    filters     = [],
    attributes  = [],
    panels      = [],
    tabs,
    form,
    showActions = [],
  } = config;

  const pluralName = `${name}s`;

  const recordTitle = (record) => {
    if (titleField) return record[titleField] ?? String(record.id);
    return record.name ?? record.decisiv_id ?? String(record.id);
  };

  // ── Index ──────────────────────────────────────────────────────────────────

  const Index = ({ canCreate = false, ...filterProps }) => {
    const {
      records, isLoading, error,
      pagination, setPage,
      setFilters,
    } = useAdminResource(endpoint);

    const tableColumns = columns.map((col) => ({
      name:     col.name,
      title:    col.title,
      DataCell: ({ rowData }) => renderCell(col, rowData, endpoint),
    }));

    return (
      <AdminPage
        title={pluralName}
        breadcrumbs={[{ text: pluralName }]}
        actions={
          canCreate && (
            <Button
              icon={PlusF}
              text={`New ${name}`}
              kind="primary"
              onClick={() => { window.location.href = `${endpoint}/new`; }}
            />
          )
        }
      >
        {filters.length > 0 && (
          <AdminFilters
            filterConfig={filters}
            onFilter={setFilters}
            filterProps={filterProps}
          />
        )}

        <AdminTable
          columns={tableColumns}
          data={records}
          isLoading={isLoading}
          error={error}
          pagination={pagination}
          onPageChange={setPage}
          getRowKey={(r) => r.id}
          emptyText={`No ${pluralName.toLowerCase()} found.`}
        />
      </AdminPage>
    );
  };

  // ── Show helpers ───────────────────────────────────────────────────────────

  const toDisplayString = (value, type) => {
    if (value == null || value === '') return '—';
    if (type === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const AttributeList = ({ attrs, record }) => (
    <div style={{
      display:             'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap:                 '16px',
    }}>
      {attrs.map((attr) => {
        const value = record[attr.name];

        if (attr.render) {
          return (
            <div key={attr.name}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#555' }}>
                {attr.label}
              </div>
              {attr.render(record)}
            </div>
          );
        }

        return (
          <TextField
            key={attr.name}
            label={attr.label}
            value={toDisplayString(value, attr.type)}
            readOnly
          />
        );
      })}
    </div>
  );

  const ShowBody = ({ record, canModify, attrs = attributes, pnls = panels }) => (
    <>
      {attrs.length > 0 && (
        <AdminPanel title="Details">
          <AttributeList attrs={attrs} record={record} />
        </AdminPanel>
      )}

      {pnls.map((panelConfig) => (
        panelConfig.type === 'subscription' ? (
          <SubscriptionPanel
            key={panelConfig.title}
            panelConfig={panelConfig}
            record={record}
          />
        ) : (
          <AssocPanel
            key={panelConfig.title}
            panelConfig={panelConfig}
            record={record}
            canModify={canModify}
          />
        )
      ))}
    </>
  );

  // ── Show ───────────────────────────────────────────────────────────────────

  const Show = ({ record, canModify = false, canDelete = false }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [activeAction, setActiveAction]   = useState(null);
    const [isActioning, setIsActioning]     = useState(false);
    const [actionError, setActionError]     = useState(null);

    const { mutate: destroy, isSubmitting: isDeleting } = useAdminMutation({
      onSuccess: () => { window.location.href = endpoint; },
    });

    const handleCustomAction = async (action) => {
      setIsActioning(true);
      setActionError(null);
      try {
        const csrf = document.querySelector('[name="csrf-token"]')?.getAttribute('content') ?? '';
        const res = await window.fetch(action.endpoint(record), {
          method:  action.method ?? 'POST',
          headers: { 'X-CSRF-Token': csrf, 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          const msg = await res.text().catch(() => '');
          setActionError(msg || `Action failed (${res.status}).`);
          setIsActioning(false);
          return;
        }
        window.location.href = action.redirectTo ?? '/';
      } catch (_) {
        setActionError('Action failed — please try again.');
        setIsActioning(false);
        setActiveAction(null);
      }
    };

    const hasActions = canModify || canDelete || showActions.length > 0;
    const actions = hasActions && (
      <Flex style={{ gap: '8px' }}>
        {showActions.map((action) => (
          <Button
            key={action.text}
            text={action.text}
            kind={action.kind ?? 'secondary'}
            onClick={() => (action.confirmTitle ? setActiveAction(action) : handleCustomAction(action))}
          />
        ))}
        {canModify && (
          <Button
            text="Edit"
            kind="secondary"
            onClick={() => { window.location.href = `${endpoint}/${record.id}/edit`; }}
          />
        )}
        {canDelete && (
          <Button
            icon={TrashF}
            text="Delete"
            kind="secondary"
            intent="danger"
            onClick={() => setConfirmDelete(true)}
          />
        )}
      </Flex>
    );

    return (
      <>
        {actionError && !activeAction && (
          <div style={{ margin: '8px 0', padding: '8px 12px', background: '#fff3f3', borderRadius: 4, border: '1px solid #f5c6cb', fontSize: 13, color: '#c0392b' }}>
            {actionError}
          </div>
        )}
        <AdminPage
          title={recordTitle(record)}
          breadcrumbs={[
            { text: pluralName, href: endpoint },
            { text: recordTitle(record) },
          ]}
          actions={actions}
        >
          {tabs ? (
            <Tabs defaultSelectedId={tabs[0].id}>
              {tabs.map((tab) => (
                <Tab key={tab.id} id={tab.id} title={tab.title}>
                  <ShowBody
                    record={record}
                    canModify={canModify}
                    attrs={tab.attributes ?? []}
                    pnls={tab.panels ?? []}
                  />
                </Tab>
              ))}
            </Tabs>
          ) : (
            <ShowBody record={record} canModify={canModify} />
          )}
        </AdminPage>

        {activeAction && (
          <AdminConfirmModal
            visible
            title={activeAction.confirmTitle}
            body={typeof activeAction.confirmBody === 'function'
              ? activeAction.confirmBody(record)
              : activeAction.confirmBody}
            confirmText={activeAction.confirmText ?? activeAction.text}
            isSubmitting={isActioning}
            error={actionError}
            onConfirm={() => handleCustomAction(activeAction)}
            onCancel={() => { setActiveAction(null); setActionError(null); }}
          />
        )}

        <AdminConfirmModal
          visible={confirmDelete}
          title={`Delete ${name}`}
          body={`Delete "${recordTitle(record)}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmIntent="danger"
          isSubmitting={isDeleting}
          onConfirm={() => destroy(`${endpoint}/${record.id}`, { method: 'DELETE' })}
          onCancel={() => setConfirmDelete(false)}
        />
      </>
    );
  };

  // ── Form (generated from config.form) ─────────────────────────────────────

  const result = { Index, Show };

  if (form) {
    const { fields } = form;
    const typeaheadFields = fields.filter((f) => f.type === 'typeahead');

    const getDefaultForm = () =>
      fields.reduce((acc, f) => {
        acc[f.name] = f.defaultValue ?? (f.type === 'checkbox' ? false : '');
        return acc;
      }, {});

    const getEditForm = (record) =>
      fields.reduce((acc, f) => {
        if (f.type === 'password') {
          acc[f.name] = '';
        } else {
          const v = record[f.name];
          acc[f.name] = v != null ? v : (f.defaultValue ?? (f.type === 'checkbox' ? false : ''));
        }
        return acc;
      }, {});

    const handleError = (err, setErrors) => {
      try {
        const body = JSON.parse(err.message);
        setErrors(body.errors ?? [err.message]);
      } catch (_) {
        setErrors([err.message]);
      }
    };

    // ── New ──────────────────────────────────────────────────────────────────

    const New = ({ ...formProps }) => {
      const [formState, setFormState] = useState(getDefaultForm);
      const [displays, setDisplays]   = useState({});
      const [errors, setErrors]       = useState([]);

      const { mutate, isSubmitting } = useAdminMutation({
        onSuccess: (record) => { window.location.href = `${endpoint}/${record.id}`; },
        onError:   (err) => handleError(err, setErrors),
      });

      const handleSubmit = (e) => {
        e.preventDefault();
        setErrors([]);
        mutate(endpoint, { body: { [modelKey]: formState } });
      };

      return (
        <AdminPage
          title={`New ${name}`}
          breadcrumbs={[
            { text: pluralName, href: endpoint },
            { text: 'New' },
          ]}
        >
          <AdminPanel title="Details">
            <form onSubmit={handleSubmit}>
              <FormFields
                fields={fields}
                formState={formState}
                setFormState={setFormState}
                displays={displays}
                setDisplays={setDisplays}
                formProps={formProps}
              />
              <ErrorBox errors={errors} />
              <Flex gap={1} marginTop={3}>
                <Button text={`Create ${name}`} kind="primary" loading={isSubmitting} loadingText="Saving…" disabled={isSubmitting} onClick={handleSubmit} />
                <Button text="Cancel" kind="secondary" onClick={() => { window.location.href = endpoint; }} />
              </Flex>
            </form>
          </AdminPanel>
        </AdminPage>
      );
    };

    // ── Edit ─────────────────────────────────────────────────────────────────

    const Edit = ({ record, ...formProps }) => {
      const [formState, setFormState] = useState(() => getEditForm(record));
      const [displays, setDisplays]   = useState(() =>
        typeaheadFields.reduce((acc, f) => {
          acc[f.name] = record[f.displayField] ?? '';
          return acc;
        }, {})
      );
      const [errors, setErrors] = useState([]);

      const { mutate, isSubmitting } = useAdminMutation({
        onSuccess: () => { window.location.href = `${endpoint}/${record.id}`; },
        onError:   (err) => handleError(err, setErrors),
      });

      const handleSubmit = (e) => {
        e.preventDefault();
        setErrors([]);
        mutate(`${endpoint}/${record.id}`, { method: 'PATCH', body: { [modelKey]: formState } });
      };

      return (
        <AdminPage
          title={`Edit ${recordTitle(record)}`}
          breadcrumbs={[
            { text: pluralName, href: endpoint },
            { text: recordTitle(record), href: `${endpoint}/${record.id}` },
            { text: 'Edit' },
          ]}
        >
          <AdminPanel title="Details">
            <form onSubmit={handleSubmit}>
              <FormFields
                fields={fields}
                formState={formState}
                setFormState={setFormState}
                displays={displays}
                setDisplays={setDisplays}
                formProps={formProps}
              />
              <ErrorBox errors={errors} />
              <Flex gap={1} marginTop={3}>
                <Button text={`Update ${name}`} kind="primary" loading={isSubmitting} loadingText="Saving…" disabled={isSubmitting} onClick={handleSubmit} />
                <Button text="Cancel" kind="secondary" onClick={() => { window.location.href = `${endpoint}/${record.id}`; }} />
              </Flex>
            </form>
          </AdminPanel>
        </AdminPage>
      );
    };

    result.New  = New;
    result.Edit = Edit;
  }

  return result;
};

export default createAdminResource;
